import { companyValues } from '../utils/data';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import CTABanner from '../components/sections/CTABanner';

function AnimatedSection({ children, delay = 0 }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#f8faff]">
      {/* Hero */}
      <div className="bg-hero-gradient pt-12 pb-24 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <p className="text-accent text-sm font-semibold tracking-widest uppercase mb-4">Who We Are</p>
          <h1 className="font-display text-5xl sm:text-6xl font-bold text-white mb-4">
            About Anir Healthcare
          </h1>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            A pharmaceutical company built on the promise of celebrating wellness and improving the health of humanity.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L60 50C120 40 240 20 360 15C480 10 600 20 720 25C840 30 960 30 1080 25C1200 20 1320 10 1380 5L1440 0V60H0Z" fill="#f8faff" />
          </svg>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Our Story */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center mb-20">
          <AnimatedSection>
            <p className="text-accent-dark text-sm font-semibold tracking-widest uppercase mb-3">Our Story</p>
            <h2 className="font-display text-4xl font-bold text-primary-800 mb-5">
              Dedicated to the Health of Humanity
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                <strong className="text-primary">Celebrating Wellness!</strong> Anir Healthcare is a pharmaceutical company dedicated to offering the best finished formulations in the healthcare sector to improve the health of humanity.
              </p>
              <p>
                We take immense pride in having an impressive product portfolio, an efficient workforce, and strong technological know-how. We possess an excellent blend of experience and expertise that makes us a force to reckon with in the pharmaceutical landscape.
              </p>
              <p>
                At Anir Healthcare, we believe in <em>Celebrating Wellness</em> — not just treating illness, but empowering every individual to live their healthiest life possible. Our formulations span across nutritional supplements, antibiotics, analgesics, vitamins, herbal extracts, and much more.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={150}>
            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="/images/anir-logo.jpg"
                  alt="Anir Healthcare"
                  className="w-full h-full object-contain bg-gradient-to-br from-blue-50 to-white p-10"
                />
              </div>
              {/* Floating stats */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-card-hover p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-2xl">💊</div>
                <div>
                  <p className="font-display text-2xl font-bold text-primary">18+</p>
                  <p className="text-xs text-slate-500 font-medium">Products</p>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 bg-primary rounded-2xl shadow-card-hover p-4 flex items-center gap-3 text-white">
                <div className="text-2xl">🏆</div>
                <div>
                  <p className="font-display text-2xl font-bold">100%</p>
                  <p className="text-xs text-blue-200 font-medium">Quality</p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
          {[
            {
              icon: '🎯',
              title: 'Our Mission',
              text: 'To deliver safe, effective, and affordable pharmaceutical formulations that uplift the quality of life for individuals and communities across India and beyond.',
              gradient: 'from-blue-50 to-indigo-50 border-blue-200',
            },
            {
              icon: '🌟',
              title: 'Our Vision',
              text: 'To be a trusted name in Indian pharmaceuticals — known for innovation, integrity, and an unwavering commitment to celebrating wellness in every product we create.',
              gradient: 'from-teal-50 to-cyan-50 border-teal-200',
            },
          ].map((item, i) => (
            <AnimatedSection key={item.title} delay={i * 150}>
              <div className={`bg-gradient-to-br ${item.gradient} border rounded-2xl p-8 h-full`}>
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="font-display text-2xl font-bold text-primary-800 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Values */}
        <AnimatedSection>
          <div className="text-center mb-12">
            <p className="text-accent-dark text-sm font-semibold tracking-widest uppercase mb-3">Core Values</p>
            <h2 className="font-display text-4xl font-bold text-primary-800">What We Stand For</h2>
            <div className="mt-4 h-1 w-16 rounded-full bg-gradient-to-r from-primary to-accent mx-auto" />
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {companyValues.map((val, i) => (
            <AnimatedSection key={val.title} delay={i * 100}>
              <div className="bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 text-center h-full">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-4">
                  {val.icon}
                </div>
                <h3 className="font-display font-bold text-primary-800 text-lg mb-2">{val.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{val.description}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Location */}
        <AnimatedSection>
          <div className="bg-white rounded-3xl shadow-card p-8 lg:p-12 flex flex-col lg:flex-row gap-8 items-start">
            <div className="flex-1">
              <p className="text-accent-dark text-sm font-semibold tracking-widest uppercase mb-3">Our Location</p>
              <h2 className="font-display text-3xl font-bold text-primary-800 mb-4">Headquartered in Bengaluru</h2>
              <p className="text-slate-600 mb-6">
                Strategically located in India's technology and innovation hub, Anir Healthcare serves healthcare professionals and patients across the country.
              </p>
              <div className="flex items-start gap-3 text-slate-600">
                <span className="text-2xl flex-shrink-0 mt-0.5">📍</span>
                <p className="text-sm leading-relaxed">
                  T-5041, Sterling Ascentia, Opposite Bellandur,<br />
                  Bengaluru, Karnataka – 560103, India
                </p>
              </div>
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
              {[
                { icon: '📧', label: 'Email', value: 'anirhealthcare@gmail.com', href: 'mailto:anirhealthcare@gmail.com' },
                { icon: '📧', label: 'Alt Email', value: 'sanjeevkusingh@gmail.com', href: 'mailto:sanjeevkusingh@gmail.com' },
                { icon: '📞', label: 'Phone 1', value: '+91 82947 03147', href: 'tel:+918294703147' },
                { icon: '📞', label: 'Phone 2', value: '+91 79798 78458', href: 'tel:+917979878458' },
              ].map((item) => (
                <a
                  key={item.value}
                  href={item.href}
                  className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors group"
                >
                  <span className="text-xl flex-shrink-0">{item.icon}</span>
                  <div>
                    <p className="text-xs text-slate-400 font-medium mb-0.5">{item.label}</p>
                    <p className="text-sm font-semibold text-primary group-hover:text-primary-700 break-all">{item.value}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>

      <CTABanner />
    </div>
  );
}
